import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    Typography,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { useDropzone } from 'react-dropzone';

import getAllStudies from 'api/study/getAllStudies';
import createFinding from 'api/study/createFinding';
import deleteFinding from 'api/study/deleteFinding';
import { enqueueGlobalUserMessageState } from 'redux/reducers/globalUserMessaging';
import { format as dateFormat } from 'date-fns';
import { getDateLocale } from 'translations/i18n';
import { parseStudyEntries } from './adminHelpers';
import { RoundedButton } from 'Components/Styles/Buttons/RoundedButton';

const EmptyState = styled.div`
    text-align: center;
    padding: 4em 0;
    color: rgba(0, 0, 0, 0.54);
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 4em 0;
`;

const StyledTableContainer = styled(TableContainer)`
    margin-top: 1em;
`;

const ActionButton = styled(Button)`
    text-transform: none;
`;

const TopBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5em;
`;

// --- Image Dropzone ---
const DropzoneContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-width: 2px;
    border-radius: 12px;
    border-color: ${props => props.isDragActive ? '#2196f3' : '#a4a4a4'};
    border-style: dashed;
    color: #bdbdbd;
    outline: none;
    transition: border .24s ease-in-out;
    height: 280px;
    cursor: pointer;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    ${props => props.preview ? `background-image: url(${props.preview});` : ''}
`;

const DropzoneText = styled(Typography)`
    font-weight: 600;
    padding-top: 8px;
`;

const FormSection = styled(Paper)`
    padding: 2em;
    margin-top: 1.5em;
`;

const FormField = styled(TextField)`
    margin-bottom: 1.5em;
`;

const SubmitArea = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 1.5em;
    gap: 12px;
`;

const SuccessMessage = styled.div`
    text-align: center;
    padding: 4em 0;
`;

function HeroImageDropzone({ image, onDrop, onClear, t }) {
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (preview) URL.revokeObjectURL(preview);
        if (image) {
            setPreview(URL.createObjectURL(image));
        } else {
            setPreview(null);
        }
        return () => { if (preview) URL.revokeObjectURL(preview); };
    }, [image]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: 'image/jpeg, image/png, image/gif',
        maxSize: 8388608,
        multiple: false,
        onDrop: accepted => { if (accepted[0]) onDrop(accepted[0]); },
    });

    return (
        <div>
            <DropzoneContainer {...getRootProps()} isDragActive={isDragActive} preview={preview}>
                <input {...getInputProps()} />
                {!preview && (
                    <React.Fragment>
                        <Typography variant="h6" color="primary">{t('admin_finding_imageTitle')}</Typography>
                        <DropzoneText variant="subtitle1">{t('admin_finding_imageDrag')}</DropzoneText>
                        <RoundedButton color="primary" variant="outlined" size="small" style={{ marginTop: '16px' }}>
                            {t('admin_finding_imageBrowse')}
                        </RoundedButton>
                    </React.Fragment>
                )}
            </DropzoneContainer>
            {image && (
                <Grid container justify="center" style={{ marginTop: '8px' }}>
                    <RoundedButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        endIcon={<NotInterestedIcon />}
                        onClick={onClear}
                    >
                        {t('admin_finding_imageClear')}
                    </RoundedButton>
                </Grid>
            )}
        </div>
    );
}

// --- Upload Form ---
function FindingUploadForm({ onBack, onSuccess, t }) {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        title: '',
        description: '',
        authors: '',
        location: '',
        publishDate: new Date().toISOString().split('T')[0],
    });
    const [heroImage, setHeroImage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    function handleChange(field) {
        return (e) => {
            const value = e.target.value;
            setForm(prev => ({ ...prev, [field]: value }));
        };
    }

    function handleSubmit() {
        setSubmitting(true);
        createFinding(form, heroImage)
            .then(() => {
                setSubmitted(true);
                dispatch(enqueueGlobalUserMessageState('success', 'admin.finding.success'));
            })
            .catch(() => {
                dispatch(enqueueGlobalUserMessageState('error', 'error.submitFailed'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

    const isValid = form.title.trim() && form.description.trim() && form.authors.trim() && form.location.trim() && form.publishDate;

    if (submitted) {
        return (
            <SuccessMessage>
                <CheckCircleIcon style={{ fontSize: 64, color: '#2cc7de' }} />
                <Typography variant="h5" style={{ marginTop: '16px' }}>
                    {t('admin_finding_successTitle')}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                    {t('admin_finding_successDescription', { study: form.title })}
                </Typography>
                <RoundedButton
                    color="primary"
                    variant="contained"
                    style={{ marginTop: '24px' }}
                    onClick={onSuccess}
                >
                    {t('admin_finding_backToList')}
                </RoundedButton>
            </SuccessMessage>
        );
    }

    return (
        <React.Fragment>
            <TopBar>
                <ActionButton startIcon={<ArrowBackIcon />} onClick={onBack}>
                    {t('admin_finding_backToList')}
                </ActionButton>
            </TopBar>

            <Typography variant="h5" style={{ marginTop: '1em' }}>
                {t('admin_finding_formTitle')}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                {t('admin_finding_formDescription')}
            </Typography>

            <FormSection elevation={1}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <HeroImageDropzone
                            image={heroImage}
                            onDrop={setHeroImage}
                            onClear={() => setHeroImage(null)}
                            t={t}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormField
                            label={t('admin_finding_titleLabel')}
                            fullWidth
                            variant="filled"
                            value={form.title}
                            onChange={handleChange('title')}
                            required
                            inputProps={{ maxLength: 100 }}
                            helperText={t('admin_finding_titleHelp')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormField
                            label={t('admin_finding_locationLabel')}
                            fullWidth
                            variant="filled"
                            value={form.location}
                            onChange={handleChange('location')}
                            required
                            placeholder="Alberta, Canada"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormField
                            label={t('admin_finding_dateLabel')}
                            type="date"
                            fullWidth
                            variant="filled"
                            value={form.publishDate}
                            onChange={handleChange('publishDate')}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormField
                            label={t('admin_finding_descriptionLabel')}
                            multiline
                            rows={6}
                            fullWidth
                            variant="filled"
                            value={form.description}
                            onChange={handleChange('description')}
                            required
                            helperText={t('admin_finding_descriptionHelp')}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormField
                            label={t('admin_finding_authorsLabel')}
                            fullWidth
                            variant="filled"
                            value={form.authors}
                            onChange={handleChange('authors')}
                            required
                            placeholder="Dr. Jane Smith, Dr. John Doe"
                            helperText={t('admin_finding_authorsHelp')}
                        />
                    </Grid>
                </Grid>

                <SubmitArea>
                    <RoundedButton variant="outlined" color="default" onClick={onBack}>
                        {t('admin_cancel')}
                    </RoundedButton>
                    <RoundedButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!isValid || submitting}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : t('admin_finding_submit')}
                    </RoundedButton>
                </SubmitArea>
            </FormSection>
        </React.Fragment>
    );
}

// --- Main Tab ---
export default function FindingsTab() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchStudies = useCallback(() => {
        setLoading(true);
        getAllStudies()
            .then(({ data }) => {
                setStudies(parseStudyEntries(data, t));
            })
            .catch(() => {
                dispatch(enqueueGlobalUserMessageState('error', 'error.generic'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, t]);

    useEffect(() => {
        fetchStudies();
    }, [fetchStudies]);

    function handleDelete() {
        if (!deleteTarget) return;
        setDeleting(true);
        deleteFinding(deleteTarget.id)
            .then(() => {
                dispatch(enqueueGlobalUserMessageState('success', 'admin_finding_deleted'));
                setDeleteTarget(null);
                fetchStudies();
            })
            .catch(() => {
                dispatch(enqueueGlobalUserMessageState('error', 'error.generic'));
            })
            .finally(() => {
                setDeleting(false);
            });
    }

    // If form is open, show the upload form
    if (showForm) {
        return (
            <FindingUploadForm
                onBack={() => setShowForm(false)}
                onSuccess={() => { setShowForm(false); fetchStudies(); }}
                t={t}
            />
        );
    }

    if (loading) {
        return (
            <LoadingContainer>
                <CircularProgress color="primary" />
            </LoadingContainer>
        );
    }

    if (studies.length === 0) {
        return (
            <EmptyState>
                <Typography variant="h6" color="textSecondary">
                    {t('admin_noActiveStudies')}
                </Typography>
            </EmptyState>
        );
    }

    const canAddFindings = studies.filter(s => s.status !== 'completed');
    const alreadyPublished = studies.filter(s => s.status === 'completed');

    return (
        <React.Fragment>
            <TopBar>
                <Typography variant="body2" color="textSecondary">
                    {t('admin_finding_listDescription')}
                </Typography>
            </TopBar>

            {canAddFindings.length > 0 && (
                <React.Fragment>
                    <Typography variant="h6" style={{ marginTop: '1em' }}>
                        {t('admin_finding_selectStudy')} ({canAddFindings.length})
                    </Typography>
                    <StyledTableContainer component={Paper} elevation={1}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('admin_studyName')}</TableCell>
                                    <TableCell>{t('admin_status')}</TableCell>
                                    <TableCell>{t('admin_lastUpdated')}</TableCell>
                                    <TableCell align="right">{t('admin_actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {canAddFindings.map(study => (
                                    <TableRow key={study.id} hover>
                                        <TableCell>{study.title}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={study.status === 'stopped' ? t('admin_statusReadyForFindings') : t('admin_statusActive')}
                                                color={study.status === 'stopped' ? 'secondary' : 'primary'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {study.date
                                                ? dateFormat(study.date, 'MMM d, yyyy', { locale: getDateLocale(i18n.language) })
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteTarget(study)}
                                                title={t('admin_finding_delete')}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                </React.Fragment>
            )}

            <TopBar style={{ marginTop: '1.5em' }}>
                <Typography variant="h6">
                    {t('admin_publishedSection')} ({alreadyPublished.length})
                </Typography>
                <RoundedButton
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowForm(true)}
                >
                    {t('admin_finding_addButton')}
                </RoundedButton>
            </TopBar>

            {alreadyPublished.length > 0 && (
                <StyledTableContainer component={Paper} elevation={1}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('admin_studyName')}</TableCell>
                                <TableCell>{t('admin_lastUpdated')}</TableCell>
                                <TableCell align="right">{t('admin_actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {alreadyPublished.map(study => (
                                <TableRow key={study.id}>
                                    <TableCell>{study.title}</TableCell>
                                    <TableCell>
                                        {study.date
                                            ? dateFormat(study.date, 'MMM d, yyyy', { locale: getDateLocale(i18n.language) })
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => setDeleteTarget(study)}
                                            title={t('admin_finding_delete')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            )}

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>{t('admin_finding_deleteTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('admin_finding_deleteConfirm', { study: deleteTarget?.title })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        {t('admin_cancel')}
                    </Button>
                    <Button onClick={handleDelete} color="secondary" disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : t('admin_finding_delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
